import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Video, Shirt, Camera, Compass, Image, Clapperboard, Users, Award,
  ArrowRight, MapPin, Calendar, DollarSign, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const moduleIcons = {
  pathways: Compass,
  portfolio: Image,
  productions: Clapperboard,
  mentorship: Users,
  certifications: Award
};

const pathwayIcons = {
  FILM: Video,
  FASHION: Shirt,
  CONTENT: Camera
};

const pathwayColors = {
  FILM: "from-violet-500 to-purple-600",
  FASHION: "from-pink-500 to-rose-600",
  CONTENT: "from-cyan-500 to-blue-600"
};

export default function LegacyStudioHub() {
  const [overview, setOverview] = useState(null);
  const [pathways, setPathways] = useState([]);
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, pathwaysRes, productionsRes] = await Promise.all([
          fetch(`${API}/api/studio/overview`),
          fetch(`${API}/api/studio/pathways`),
          fetch(`${API}/api/studio/productions`)
        ]);
        
        if (overviewRes.ok) setOverview(await overviewRes.json());
        if (pathwaysRes.ok) {
          const data = await pathwaysRes.json();
          setPathways(data.pathways || []);
        }
        if (productionsRes.ok) {
          const data = await productionsRes.json();
          setProductions(data.productions || []);
        }
      } catch (err) {
        console.error('Error fetching studio data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="legacy-studio-hub">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-500 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="bg-violet-500/30 text-violet-200 border-violet-400 mb-4">F6: Fashion/Film</Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              LegacyStudio
            </h1>
            <p className="text-xl text-violet-200 mb-6 max-w-2xl">
              Your creative career starts here. Fashion. Film. Future.
              Build your portfolio, land real gigs, connect with mentors.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/legacystudio/pathways">
                <Button size="lg" className="bg-white text-violet-900 hover:bg-violet-50">
                  <Compass className="mr-2 h-5 w-5" />
                  Explore Pathways
                </Button>
              </Link>
              <Link to="/legacystudio/productions">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Clapperboard className="mr-2 h-5 w-5" />
                  View Productions
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-black/30 border-y border-violet-500/30 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{overview?.stats?.active_productions || 3}</div>
              <p className="text-violet-300 text-sm">Active Productions</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{overview?.stats?.open_positions || 20}</div>
              <p className="text-violet-300 text-sm">Open Positions</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{overview?.tracks || 15}</div>
              <p className="text-violet-300 text-sm">Career Tracks</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{overview?.stats?.mentors_available || 3}</div>
              <p className="text-violet-300 text-sm">Available Mentors</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 space-y-16">
        {/* Creative Pathways */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">Creative Pathways</h2>
            <p className="text-violet-300">Choose your creative journey</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pathways.map((pathway, idx) => {
              const Icon = pathwayIcons[pathway.id] || Camera;
              const colorClass = pathwayColors[pathway.id] || "from-gray-500 to-gray-600";
              
              return (
                <motion.div
                  key={pathway.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={`/legacystudio/pathways/${pathway.id}`}>
                    <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all group cursor-pointer">
                      <CardHeader>
                        <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClass} text-white w-fit mb-3 group-hover:scale-110 transition-transform`}>
                          <Icon className="h-8 w-8" />
                        </div>
                        <CardTitle className="text-white text-xl">{pathway.name}</CardTitle>
                        <CardDescription className="text-violet-200">
                          {pathway.tracks?.length} career tracks
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 mb-4">
                          {pathway.tracks?.slice(0, 3).map(track => (
                            <div key={track.id} className="flex items-center justify-between text-sm">
                              <span className="text-violet-100">{track.name}</span>
                              <span className="text-violet-400">{track.wage_range}</span>
                            </div>
                          ))}
                          {pathway.tracks?.length > 3 && (
                            <p className="text-violet-400 text-xs">+{pathway.tracks.length - 3} more tracks</p>
                          )}
                        </div>
                        <span className="text-violet-400 text-sm font-medium flex items-center">
                          Explore Pathway <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Active Productions */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Now Hiring</h2>
              <p className="text-violet-300">Real opportunities on real sets</p>
            </div>
            <Link to="/legacystudio/productions">
              <Button variant="outline" className="border-violet-400 text-violet-200 hover:bg-violet-500/20">
                View All Productions
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {productions.map((production, idx) => {
              const openRoles = production.roles_needed?.reduce(
                (acc, r) => acc + (r.slots - r.filled), 0
              ) || 0;
              
              return (
                <motion.div
                  key={production.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all">
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-violet-400 text-violet-200">
                          {production.type}
                        </Badge>
                        {production.cultural_focus && (
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-400">
                            Cultural
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-white">{production.title}</CardTitle>
                      <CardDescription className="text-violet-200">
                        {production.production_company}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-violet-100 text-sm mb-4 line-clamp-2">{production.description}</p>
                      
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-center text-violet-300">
                          <MapPin className="h-4 w-4 mr-2" />
                          {production.location}
                        </div>
                        <div className="flex items-center text-violet-300">
                          <Calendar className="h-4 w-4 mr-2" />
                          Starts {formatDate(production.start_date)}
                        </div>
                        <div className="flex items-center text-emerald-400">
                          <Briefcase className="h-4 w-4 mr-2" />
                          {openRoles} positions open
                        </div>
                      </div>
                      
                      <Link to={`/legacystudio/productions/${production.id}`}>
                        <Button className="w-full bg-violet-600 hover:bg-violet-700">
                          Apply Now
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Core Modules */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Platform Modules</h2>
            <p className="text-violet-300">Everything you need to build your creative career</p>
          </div>
          
          <div className="grid md:grid-cols-5 gap-4">
            {overview?.modules?.map((module, idx) => {
              const Icon = moduleIcons[module.id] || Compass;
              const links = {
                pathways: "/legacystudio/pathways",
                portfolio: "/legacystudio/portfolio",
                productions: "/legacystudio/productions",
                mentorship: "/legacystudio/mentors",
                certifications: "/legacystudio/certifications"
              };
              
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={links[module.id] || "/legacystudio"}>
                    <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all text-center cursor-pointer group">
                      <CardContent className="py-6">
                        <div className="p-3 rounded-xl bg-violet-500/20 text-violet-300 w-fit mx-auto mb-3 group-hover:bg-violet-500/40 transition-colors">
                          <Icon className="h-6 w-6" />
                        </div>
                        <h3 className="font-medium text-white text-sm">{module.name}</h3>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Portfolio Spotlight */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Portfolio Spotlight</h2>
              <p className="text-violet-300">Showcasing top community talent</p>
            </div>
            <Link to="/legacystudio/portfolio">
              <Button variant="outline" className="border-violet-400 text-violet-200 hover:bg-violet-500/20">
                View All Portfolios
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { name: "Jasmine Williams", specialty: "Film Director", image: "https://images.unsplash.com/photo-1671882230687-cd2640733a21?w=400", work: "Award-winning shorts filmmaker" },
              { name: "Marcus Thompson", specialty: "Fashion Designer", image: "https://images.pexels.com/photos/8276676/pexels-photo-8276676.jpeg?auto=compress&cs=tinysrgb&w=400", work: "Sustainable streetwear creator" },
              { name: "Aaliyah Davis", specialty: "Visual Artist", image: "https://images.pexels.com/photos/6925419/pexels-photo-6925419.jpeg?auto=compress&cs=tinysrgb&w=400", work: "Documentary & music videos" },
              { name: "Devon Jackson", specialty: "Content Creator", image: "https://images.unsplash.com/photo-1648025933224-b7d1f2e49e82?w=400", work: "500K+ social following" }
            ].map((creator, idx) => (
              <motion.div
                key={creator.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all overflow-hidden group">
                  <div className="aspect-square relative">
                    <img 
                      src={creator.image} 
                      alt={creator.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <Badge className="bg-violet-500/80 text-white mb-2">{creator.specialty}</Badge>
                      <h3 className="font-semibold text-white">{creator.name}</h3>
                      <p className="text-violet-200 text-sm">{creator.work}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section>
          <Card className="bg-gradient-to-br from-violet-600 to-purple-700 border-0">
            <CardContent className="py-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Start Your Creative Journey?
              </h2>
              <p className="text-violet-100 mb-8 max-w-xl mx-auto">
                Take our pathway assessment to discover which creative career fits you best.
                Get personalized recommendations and start building your portfolio today.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/legacystudio/pathways">
                  <Button size="lg" className="bg-white text-violet-700 hover:bg-violet-50">
                    <Compass className="mr-2 h-5 w-5" />
                    Find Your Path
                  </Button>
                </Link>
                <Link to="/legacystudio/portfolio">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <Image className="mr-2 h-5 w-5" />
                    Build Portfolio
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
